import { UID } from 'agora-rtc-react';
import { apiService } from '../services/apiService';
import {
  APIResponse,
  AttendResponse,
  AuthenticateParams,
  FeedBackParams,
  LeadParams,
  PollResponsesResponse,
  StatsResponse
} from './interface';
import { poll } from '@/utils/interface';

const NODE_API = import.meta.env.VITE_PUBLIC_NODE_URL;
const CRYSTAL_API = import.meta.env.VITE_PUBLIC_CRYSTAL_API_URL;
const CRYSTAL_JAVA_API = import.meta.env.VITE_PUBLIC_CRYSTAL_API_JAVA_URL;
const JAVA_API = import.meta.env.VITE_PUBLIC_CRYSTAL_API_BASE_URL;

/* Dashboard */

export const getLeads = (params: URLSearchParams) =>
  apiService.get<APIResponse>(`${CRYSTAL_JAVA_API}/getLeads`, params);

export const getReferrals = (params: URLSearchParams) =>
  apiService.get<APIResponse>(`${CRYSTAL_JAVA_API}/getReferredUsers`, params);

export const getGuests = (params: URLSearchParams) =>
  apiService.get<APIResponse>(`${CRYSTAL_JAVA_API}/guest/dashboard`, params);

export const getClasses = (params: URLSearchParams) =>
  apiService.get<APIResponse>(`${CRYSTAL_JAVA_API}/dashboard`, params);

export const getClassData = (params: URLSearchParams) =>
  apiService.get<APIResponse>(`${CRYSTAL_JAVA_API}/attendees`, params);

/* Authentication */

export const login = ({ email, password }: { email: string; password: string }) =>
  apiService.post<APIResponse>(`${CRYSTAL_API}/login`, {
    email,
    password
  });

export const authenticate = ({ psid, mobileNo, roomId, referredBy }: AuthenticateParams) =>
  apiService.get<APIResponse>(`${CRYSTAL_JAVA_API}/authenticate`, {
    psid,
    roomId,
    mobileNo,
    referredBy
  });

export const sendOtp = ({ mobile_number, source }: AuthenticateParams) =>
  apiService.post<APIResponse>(`${CRYSTAL_JAVA_API}/otp`, {
    mobile_number,
    source
  });

export const verifyOtp = ({ mobile_number, otp, source }: AuthenticateParams) =>
  apiService.post<APIResponse>(`${CRYSTAL_JAVA_API}/otp/verify`, {
    mobile_number,
    otp,
    source
  });

export const createLead = ({
  city,
  contact_person_name,
  course_id,
  email_id,
  mobile_number,
  source
}: LeadParams) =>
  apiService.post<APIResponse>(`${CRYSTAL_JAVA_API}/lead`, {
    city,
    contact_person_name,
    course_id,
    email_id,
    mobile_number,
    source
  });

// Feedback
export const sendFeedback = ({
  roomId,
  userId,
  userType,
  rating,
  facultyRating,
  technicalRating,
  comment
}: FeedBackParams) =>
  apiService.post<APIResponse>(`${CRYSTAL_JAVA_API}/feedback`, {
    roomId,
    userId,
    userType,
    rating,
    facultyRating,
    technicalRating,
    comment
  });

export const joinClass = (encId: string, isGuest: boolean) =>
  apiService.get<AttendResponse>(`${CRYSTAL_JAVA_API}/attend/${isGuest ? 'guest/' : ''}${encId}`);

export const getToken = (channelId: string, userId: UID) =>
  apiService.post<APIResponse>(`${NODE_API}/agora/generateToken`, {
    channelId,
    userId
  });

export const getBytePlusToken = (channelId: string, userId: UID) =>
  apiService.post<APIResponse>(`${JAVA_API}/byteplus/generateToken`, {
    channelId,
    userId
  });

export const uploadFile = (
  file: File,
  roomId: string,
  fileTypeInStr: string,
  longTimeout = false
) =>
  apiService.post<APIResponse>(
    `${CRYSTAL_API}/uploadFiles`,
    { file, roomId, fileTypeInStr },
    {
      headers: { 'content-type': 'multipart/form-data' },
      ...(fileTypeInStr === 'Board' || longTimeout ? { timeout: 60000 } : {})
    }
  );

export const createPoll = (payload: Partial<poll>) => {
  return apiService.post<poll>(`${NODE_API}/poll/create`, payload);
};

export const getStats = (roomId: number | null) => {
  return apiService.get<StatsResponse>(`${CRYSTAL_JAVA_API}/engagementStats`, { roomId });
};

export const getPolls = (roomId: number | null) => {
  return apiService.get<APIResponse>(`${NODE_API}/poll/${roomId}`);
};

export const getPollResponses = (id: string) => {
  return apiService.get<PollResponsesResponse>(`${NODE_API}/poll/responses/${id}`);
};

export const logError = (
  roomId: number,
  userIdType: number,
  userId: UID,
  exceptionType: string | number,
  description: string
) => {
  const postData = {
    roomId,
    userIdType,
    userId,
    exceptionType,
    description
  };

  return apiService.post<APIResponse>(`${CRYSTAL_JAVA_API}/log`, postData);
};

export const stopRecording = (roomId: number, pdfUploaded = false) =>
  apiService.post<APIResponse>(
    `${CRYSTAL_API}/stopRecording?roomId=${roomId}&pdfUploaded=${pdfUploaded}`
  );
